import { useCallback, useRef } from "react";

interface ColorSliderProps {
    selectedColor: string;
    onColorChange: (color: string) => void;
    /** Set to true while interacting, to suppress text editor blur-confirm */
    suppressBlurRef?: React.MutableRefObject<boolean>;
}

/**
 * Convert a ratio (0–1) along the slider track to a hex color.
 * Matches WhatsApp order: white → black → red → rainbow → pink
 */
function ratioToColor(ratio: number): string {
    // 0.00–0.06: white
    // 0.06–0.14: white → black
    // 0.14–1.00: black → red → orange → yellow → green → cyan → blue → purple → magenta → pink
    if (ratio <= 0.06) {
        return "#FFFFFF";
    }
    if (ratio <= 0.14) {
        // White → black
        const t = (ratio - 0.06) / 0.08;
        const v = Math.round(255 * (1 - t));
        const hex = v.toString(16).padStart(2, "0");
        return `#${hex}${hex}${hex}`.toUpperCase();
    }
    // Rainbow spectrum: map 0.14–1.0 to hue 0–360
    const hue = ((ratio - 0.14) / 0.86) * 360;
    return hslToHex(hue, 100, 50);
}

function hslToHex(h: number, s: number, l: number): string {
    const sNorm = s / 100;
    const lNorm = l / 100;
    const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = lNorm - c / 2;

    let r = 0, g = 0, b = 0;
    if (h < 60) { r = c; g = x; }
    else if (h < 120) { r = x; g = c; }
    else if (h < 180) { g = c; b = x; }
    else if (h < 240) { g = x; b = c; }
    else if (h < 300) { r = x; b = c; }
    else { r = c; b = x; }

    const toHex = (v: number) =>
        Math.round((v + m) * 255).toString(16).padStart(2, "0");

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Find the closest ratio on the gradient for a given hex color.
 */
function colorToRatio(hex: string): number {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;

    // Near white
    if (l > 0.95 && max - min < 0.05) return 0.03;
    // Near black
    if (l < 0.08) return 0.14;

    // Grayscale
    if (max - min < 0.05) {
        const t = 1 - l;
        return 0.06 + t * 0.08;
    }

    // Compute hue
    const d = max - min;
    let h = 0;
    if (d > 0) {
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
        else if (max === g) h = ((b - r) / d + 2) * 60;
        else h = ((r - g) / d + 4) * 60;
    }

    // Map hue to ratio in the spectrum region (0.14–1.0)
    return 0.14 + (h / 360) * 0.86;
}

// WhatsApp-style gradient: white → gray → black → rainbow spectrum
const GRADIENT = `linear-gradient(
    to bottom,
    #FFFFFF 0%,
    #FFFFFF 6%,
    #808080 10%,
    #000000 14%,
    #FF0000 20%,
    #FF6600 28%,
    #FFCC00 36%,
    #66FF00 44%,
    #00FF66 50%,
    #00FFFF 58%,
    #0066FF 66%,
    #0000FF 72%,
    #6600FF 78%,
    #CC00FF 84%,
    #FF00CC 90%,
    #FF0066 100%
)`;

export function ColorSlider({ selectedColor, onColorChange, suppressBlurRef }: ColorSliderProps) {
    const trackRef = useRef<HTMLDivElement>(null);
    const draggingRef = useRef(false);

    const thumbPosition = colorToRatio(selectedColor);

    const getColorFromY = useCallback(
        (clientY: number) => {
            const track = trackRef.current;
            if (!track) return selectedColor;
            const rect = track.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
            return ratioToColor(ratio);
        },
        [selectedColor]
    );

    const handlePointerDown = useCallback(
        (e: React.PointerEvent) => {
            e.stopPropagation();
            e.preventDefault();
            draggingRef.current = true;
            if (suppressBlurRef) suppressBlurRef.current = true;
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            onColorChange(getColorFromY(e.clientY));
        },
        [getColorFromY, onColorChange, suppressBlurRef]
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent) => {
            if (!draggingRef.current) return;
            e.stopPropagation();
            onColorChange(getColorFromY(e.clientY));
        },
        [getColorFromY, onColorChange]
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent) => {
            if (!draggingRef.current) return;
            e.stopPropagation();
            draggingRef.current = false;
            if (suppressBlurRef) suppressBlurRef.current = false;
        },
        [suppressBlurRef]
    );

    return (
        <div
            className="absolute right-4 flex flex-col items-center z-30"
            style={{ top: "calc(max(1rem, env(safe-area-inset-top, 1rem)) + 4.5rem)", touchAction: "none" }}
            onPointerDown={(e) => e.stopPropagation()}
        >
            <div
                ref={trackRef}
                className="relative rounded-full border-2 border-white/80"
                style={{ background: GRADIENT, height: 200, width: 10 }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            >
                {/* Thumb */}
                <div
                    className="absolute left-1/2 -translate-x-1/2 w-4.5 h-4.5 rounded-full border-2 border-white"
                    style={{
                        top: `calc(${thumbPosition * 100}% - 9px)`,
                        backgroundColor: selectedColor,
                        pointerEvents: "none",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
                    }}
                />
            </div>
        </div>
    );
}
